import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import './BalloonPopGame.css';
import { QUESTIONS, BALLOON_COLORS } from './BalloonPopData';

export default function BalloonPopGame() {
  // Toast system with proper notifications
  const [toasts, setToasts] = useState([]);
  
  const toast = ({ title, description, type = 'info' }) => {
    const id = Math.random().toString(36).substring(2, 9);
    const newToast = { id, title, description, type };
    setToasts(prev => [...prev, newToast]);
    
    // Auto-remove after 3 seconds
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3000);
  };
  
  const animationRef = useRef(0);
  const popSoundRef = useRef(null);
  const gameAreaRef = useRef(null);
  const websocketRef = useRef(null);
  const resizeTimeoutRef = useRef(null);
  const connectionTimeoutRef = useRef(null);
  const timerRef = useRef(null);
  
  // Generate a unique player ID for this session
  const playerIdRef = useRef(`player_${Math.random().toString(36).substring(2, 10)}`);

  // Game settings
  const [settings, setSettings] = useState({
    balloonCount: 4,
    timePerQuestion: 20,
    speedMultiplier: 1,
    categories: ['math', 'science', 'technology', 'engineering'],
    difficulty: 'easy',
  });
  
  // Ref for settings to avoid timer resets
  const settingsRef = useRef(settings);
  
  // Update settings ref when settings change
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Game state
  const [gameState, setGameState] = useState({
    score: 0,
    level: 1,
    lives: 3,
    currentQuestion: null,
    balloons: [],
    gameStatus: 'ready',
    timeLeft: 15,
    streak: 0,
  });

  // WebSocket state
  const [wsConnectionStatus, setWsConnectionStatus] = useState('Not connected');
  const [roomName, setRoomName] = useState('game_room');
  
  // State for the explanation dialog
  const [showExplanation, setShowExplanation] = useState(false);
  const [lastResult, setLastResult] = useState(false);
  const [answeredQuestion, setAnsweredQuestion] = useState(null);
  
  // Window size state
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  });

  // Handle window resize
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Update window size state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      });
      
      // If we have a game in progress, recreate balloons for better positioning
      if (gameState.gameStatus === 'playing' && gameState.currentQuestion) {
        clearTimeout(resizeTimeoutRef.current);
        resizeTimeoutRef.current = setTimeout(() => {
          setGameState(prevState => ({
            ...prevState,
            balloons: createBalloons(prevState.currentQuestion, settingsRef.current.balloonCount)
          }));
        }, 250); // Debounce resize event
      }
    }
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Call handler right away so state gets updated with initial window size
    handleResize();
    
    // Remove event listener on cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(resizeTimeoutRef.current);
    };
  }, [gameState.gameStatus, gameState.currentQuestion]); // Re-run if these dependencies change

  // Connect to WebSocket
  const connectWebSocket = () => {
    // Close existing connection if any
    if (websocketRef.current) {
      websocketRef.current.close();
    }

    // Connect to production on port 8003
    const wsURL = `wss://api.andrewslearning.com/wschat/balloonpop/${roomName}/`;
    
    console.log(`Connecting to WebSocket: ${wsURL}`);
    setWsConnectionStatus(`Connecting to ${wsURL}...`);
    
    try {
      const ws = new WebSocket(wsURL);
      websocketRef.current = ws;
      
      // Add a timeout to detect connection failures
      connectionTimeoutRef.current = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('Connection timeout, trying port 8004...');
          ws.close();
          tryPort8004Connection();
        }
      }, 5000);
      
      ws.onopen = () => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setWsConnectionStatus('Connected');
        console.log('WebSocket connection established');
        
        // Join the game
        const joinMessage = JSON.stringify({
          type: 'join_game',
          name: `Player_${Math.floor(Math.random() * 1000)}`,
          player_id: playerIdRef.current,
          topics: settingsRef.current.categories
        });
        
        ws.send(joinMessage);
        console.log('Sent join message:', joinMessage);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received WebSocket message:', data);
          
          switch(data.type) {
            case 'welcome_message':
              console.log('Welcome message:', data.message);
              break;
            case 'question':
            case 'new_question':
              handleWebSocketQuestion(data);
              break;
            case 'answer_result':
              handleWebSocketAnswerResult(data);
              break;
            case 'game_state':
              handleWebSocketGameState(data);
              break;
            case 'error':
              console.error('WebSocket error from server:', data.message);
              toast({
                title: "Error",
                description: data.message
              });
              break;
            default:
              console.log('Unhandled message type:', data.type);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };
      
      ws.onclose = (event) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setWsConnectionStatus(`Disconnected (code: ${event.code})`);
        console.log(`WebSocket connection closed: ${event.code} - ${event.reason || 'No reason provided'}`);
        
        // Try port 8004 if closed unexpectedly
        if (event.code !== 1000 && event.code !== 1001) {
          console.log('Connection to production failed. Trying port 8004...');
          tryPort8004Connection();
        }
      };
      
      ws.onerror = (error) => {
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
        setWsConnectionStatus('Error');
        console.error('WebSocket error:', error);
        
        // Display error to user
        toast({
          title: "Connection Error",
          description: "Failed to connect to production server. Trying test server..."
        });
        
        // Try test server on port 8004
        tryPort8004Connection();
      };
    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      setWsConnectionStatus('Connection Failed');
      
      toast({
        title: "Connection Error",
        description: "Failed to connect to production server. Trying test server..."
      });
      
      // Try test server on port 8004
      tryPort8004Connection();
    }
  };
  
  // Try connecting to test server on port 8004
  const tryPort8004Connection = () => {
    try {
      const testURL = `wss://api.andrewslearning.com:8004/wschat/balloonpop/${roomName}/`;
      console.log(`Trying test server on port 8004: ${testURL}`);
      setWsConnectionStatus(`Connecting to test server...`);
      
      const ws = new WebSocket(testURL);
      websocketRef.current = ws;
      
      // Add a timeout to detect connection failures
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('Connection timeout on test server, trying localhost...');
          ws.close();
          tryLocalhost();
        }
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        setWsConnectionStatus('Connected (Test Server)');
        console.log('WebSocket connection established to test server');
        
        // Join the game
        const joinMessage = JSON.stringify({
          type: 'join_game',
          name: `Player_${Math.floor(Math.random() * 1000)}`,
          player_id: playerIdRef.current,
          topics: settingsRef.current.categories
        });
        
        ws.send(joinMessage);
        console.log('Sent join message to test server:', joinMessage);
      };
      
      // Event handlers similar to primary connection
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received WebSocket message from test server:', data);
          
          // Process message normally using the same handlers
          switch(data.type) {
            case 'welcome_message':
              console.log('Welcome message from test server:', data.message);
              break;
            case 'question':
            case 'new_question':
              handleWebSocketQuestion(data);
              break;
            case 'answer_result':
              handleWebSocketAnswerResult(data);
              break;
            case 'game_state':
              handleWebSocketGameState(data);
              break;
            case 'error':
              console.error('WebSocket error from test server:', data.message);
              break;
            default:
              console.log('Unhandled message type from test server:', data.type);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message from test server:', error);
        }
      };
      
      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        setWsConnectionStatus(`Disconnected from test server (code: ${event.code})`);
        console.log(`WebSocket connection to test server closed: ${event.code}`);
        
        // Try localhost as last resort
        tryLocalhost();
      };
      
      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error from test server:', error);
        setWsConnectionStatus('All Remote Connections Failed');
        
        // Try localhost
        toast({
          title: "Both Remote Connections Failed",
          description: "Trying localhost connection..."
        });
        
        tryLocalhost();
      };
    } catch (error) {
      console.error('Error creating WebSocket connection to test server:', error);
      
      // Try localhost
      toast({
        title: "Both Remote Connections Failed",
        description: "Trying localhost connection..."
      });
      
      tryLocalhost();
    }
  };
  
  // Handle WebSocket question
  const handleWebSocketQuestion = (data) => {
    console.log('Processing question from WebSocket:', data);
    
    // Extract question data, handling both direct format and nested format
    const questionData = data.question || data;
    
    // Construct a question object in the format our game expects
    const question = {
      id: questionData.id || `q_${Date.now()}`,
      text: questionData.text || 'No question text provided',
      options: questionData.options?.map(opt => opt.text) || [],
      correctAnswer: questionData.correct_option_index || 0,
      category: questionData.category || 'General',
      difficulty: questionData.difficulty || 'medium',
      explanation: questionData.explanation || 'No explanation provided'
    };
    
    console.log('Constructed question:', question);
    
    // Update game state with the new question
    setGameState(prevState => ({
      ...prevState,
      currentQuestion: question,
      balloons: createBalloons(question, settingsRef.current.balloonCount),
      timeLeft: settingsRef.current.timePerQuestion
    }));
  };
  
  // Handle WebSocket answer result
  const handleWebSocketAnswerResult = (data) => {
    console.log('Processing answer result from WebSocket:', data);
    
    // Update game state with the result
    setGameState(prevState => ({
      ...prevState,
      score: data.score || prevState.score,
      lives: data.lives || prevState.lives,
      streak: data.streak || prevState.streak,
      level: data.level || prevState.level
    }));
    
    // Show explanation if available
    if (data.correct !== undefined) {
      setLastResult(data.correct);
      setShowExplanation(true);
      
      // After a delay, hide the explanation
      setTimeout(() => {
        setShowExplanation(false);
        setAnsweredQuestion(null); // Clear the answered question after explanation is closed
      }, 3000);
    }
    
    // Check for game over
    if (data.game_over) {
      setGameState(prevState => ({
        ...prevState,
        gameStatus: 'gameOver'
      }));
    }
  };
  
  // Handle WebSocket game state update
  const handleWebSocketGameState = (data) => {
    console.log('Processing game state from WebSocket:', data);
    
    // Update game state
    setGameState(prevState => ({
      ...prevState,
      score: data.score || prevState.score,
      lives: data.lives || prevState.lives,
      streak: data.streak || prevState.streak,
      level: data.level || prevState.level
    }));
  };
  
  // Initialize the game with proper cleanup
  useEffect(() => {
    // Create pop sound effect
    popSoundRef.current = new Audio('data:audio/wav;base64,UklGRuQFAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YcAFAACAgICAgICAgICAgICAgICAgICAgICAgICBgYGBgoGBgoGCgYOCgoKDg4KEhISEhYWFhoWHhoiHiIiJiYqJiouKjIuMjI2MjY6Oj5CQkJGRkpKTkpOUlJWVlpaXl5iYmZmampubm5ycnZ2enp+foKChoqKio6OkpKWlpqaoqKmpqqqrrKytra6ur7CwsbGysrOztLS1tba2t7e4uLm5urq7u7y8vb2+vr+/wMDAwcHCwsPDxMTFxcbGx8fIyMnJysrLy8zMzc3Ozs/P0NDR0dLS09PU1NXV1tbX19jY2dnZ2tra29vc3N3d3t7f3+Dg4eHi4uPj5OTl5ebm5+fo6Onp6urr6+zs7e3u7u/v8PDx8fLy8/P09PX19vb39/j4+fn6+vv7/Pz9/f7+//8AAAB/gH+Af4B/gH+Af4B/gH+Af4B/gH+AgICAgIF/gn+Cf4J/g3+DgIOBhIGEgYWBhoGHgYiBiIKJgomDioSLhIyFjIaNh46HkIiQiZGKkouTjJONlI6UjpWQlpGXkpiTmZSalZuWnJecmJ2ZnpqfnKCdoZ6in6OgpKGloqajqKSppaqmq6esqK2prqqvq7CssayyrLOutK+1sLaxuLK5s7q0u7W8tr23vri/ucC6wbvCvMO9xL7Fv8bAx8HIwsnDysTLxczGzcfOyM/J0MrRy9LM083UztXP1tDX0djS2dPa1NvV3Nbd197Y39nh2uLb493k3uXf5uDn4ejj6eTq5evn7Ojs6e3q7uvv7PDt8e7y7/Pw9PH18vbz9/T49fn2+vf7+Pz5/fr++/8AAIGBgYGBgYGBgYGBgYGBgYGBgYGBgYGCgoKCgoKCgoKCgoKCgoKCgoKCgoKDg4ODg4ODg4ODg4ODg4ODg4ODhISEhISEhISEhISEhISEhISEhISFhYWFhYWFhYWFhYWFhYWFhYWFhYaGhoaGhoaGhoaGhoaGhoaGhoaGh4eHh4eHh4eHh4eHh4eHh4eHh4iIiIiIiIiIiIiIiIiIiIiIiIiJiYmJiYmJiYmJiYmJiYmJiYmJiYqKioqKioqKioqKioqKioqKioqKi4uLi4uLi4uLi4uLi4uLi4uLi4yMjIyMjIyMjIyMjIyMjIyMjIyMjY2NjY2NjY2NjY2NjY2NjY2NjY6Ojo6Ojo6Ojo6Ojo6Ojo6Ojo6Pj4+Pj4+Pj4+Pj4+Pj4+Pj4+PkJCQkJCQkJCQkJCQkJCQkJCQkJCRkZGRkZGRkZGRkZGRkZGRkZGRkZGSkpKSkpKSkpKSkpKSkpKSkpKSkpKTk5OTk5OTk5OTk5OTk5OTk5OTk5OUlJSUlJSUlJSUlJSUlJSUlJSUlJSVlZWVlZWVlZWVlZWVlZWVlZWVlZWWlpaWlpaWlpaWlpaWlpaWlpaWlpaXl5eXl5eXl5eXl5eXl5eXl5eXl5eYmJiYmJiYmJiYmJiYmJiYmJiYmJiYmZmZmZmZmZmZmZmZmZmZmZmZmZmZmpqampqampqampqampqampqampqam5ubm5ubm5ubm5ubm5ubm5ubm5ubm5ycnJycnJycnJycnJycnJycnJycnJydnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ2dnZ6enp6enp6enp6enp6enp6enp6enp6fn5+fn5+fn5+fn5+fn5+fn5+fn5+foKCgoKCgoA==');

    return () => {
      // Cleanup animation frame
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = 0;
      }
      
      // Cleanup timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      // Cleanup timeouts
      if (resizeTimeoutRef.current) {
        clearTimeout(resizeTimeoutRef.current);
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
      
      // Cleanup WebSocket
      if (websocketRef.current) {
        websocketRef.current.close();
        websocketRef.current = null;
      }
      
      // Cleanup confetti canvases
      const confettiCanvases = document.querySelectorAll('.balloon-pop-confetti-canvas');
      confettiCanvases.forEach(canvas => {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
    };
  }, []);
  
  // Establish WebSocket connection on component mount
  useEffect(() => {
    connectWebSocket();
    return () => {
      if (websocketRef.current) {
        websocketRef.current.close();
      }
    };
  }, [roomName]);

  // Clean up any confetti canvases and animations when component unmounts
  useEffect(() => {
    return () => {
      // Remove any confetti canvases from the DOM
      const confettiCanvases = document.querySelectorAll('.balloon-pop-confetti-canvas');
      confettiCanvases.forEach(canvas => {
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      });
    };
  }, []);

  // Handle timer with proper cleanup and dependencies
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    if (gameState.gameStatus === 'playing') {
      timerRef.current = setInterval(() => {
        setGameState(prevState => {
          if (prevState.timeLeft <= 1) {
            const newLives = prevState.lives - 1;
            if (newLives <= 0) {
              return {
                ...prevState,
                timeLeft: 0,
                gameStatus: 'gameOver',
                lives: 0
              };
            }
            // Only reset question/balloons when time runs out
            const newQuestion = getRandomQuestion();
            return {
              ...prevState,
              timeLeft: settingsRef.current.timePerQuestion,
              lives: newLives,
              streak: 0,
              currentQuestion: newQuestion,
              balloons: createBalloons(newQuestion, settingsRef.current.balloonCount)
            };
          }
          return {
            ...prevState,
            timeLeft: prevState.timeLeft - 1
          };
        });
      }, 1000);
    }
    
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [gameState.gameStatus, gameState.timeLeft]); 

  // Get a random question based on current level (memoized)
  const getRandomQuestion = useCallback(() => {
    let difficulty;
    if (gameState.level <= 3) {
      difficulty = 'easy';
    } else if (gameState.level <= 7) {
      difficulty = 'medium';
    } else {
      difficulty = 'hard';
    }
    const filteredQuestions = QUESTIONS.filter(q => q.difficulty === difficulty);
    const availableQuestions = filteredQuestions.length > 0 ? filteredQuestions : QUESTIONS;
    const randomIndex = Math.floor(Math.random() * availableQuestions.length);
    return availableQuestions[randomIndex];
  }, [gameState.level]);

  // Create balloons for a question (memoized for performance)
  const createBalloons = useCallback((question, count) => {
    if (!question) return [];
    
    const balloons = [];
    const containerWidth = gameAreaRef.current?.clientWidth || windowSize.width * 0.8;
    const containerHeight = windowSize.width < 600 ? 400 : 500; // Responsive height
    
    // Adjust balloon size based on screen width and count
    const baseBalloonSize = windowSize.width < 600 
      ? 100 + Math.min(windowSize.width / 20, 30) // Smaller on mobile
      : 150 + Math.min(windowSize.width / 40, 30); // Larger on desktop
    
    for (let i = 0; i < count; i++) {
      if (i < question.options.length) {
        // Use 95% of container width to spread balloons further apart horizontally
        const xPosition = (containerWidth * 1.1 / (count + 1)) * (i + 1) + (containerWidth * 0.015);
        // Calculate balloon size - slight variation between balloons
        const balloonSize = baseBalloonSize + (Math.random() * 20);
        
        balloons.push({
          id: i,
          x: xPosition,
          y: containerHeight - 50,
          size: balloonSize,
          // Slow down balloon speed slightly to give more reading time
          speed: (0.25 + Math.random() * 0.25) * settingsRef.current.speedMultiplier,
          text: question.options[i],
          colorScheme: BALLOON_COLORS[i % BALLOON_COLORS.length],
          optionIndex: i
        });
      }
    }
    return balloons;
  }, [windowSize.width]);

  // Reset game with proper settings sync
  const resetGame = () => {
    const resetSettings = {
      balloonCount: 4,
      timePerQuestion: 25,
      speedMultiplier: 1,
      categories: ['math', 'science', 'technology', 'engineering'],
      difficulty: 'easy'
    };
    
    setSettings(resetSettings);
    settingsRef.current = resetSettings;

    const initialQuestion = getRandomQuestion();
    
    setGameState({
      score: 0,
      level: 1,
      lives: 3,
      currentQuestion: initialQuestion,
      balloons: createBalloons(initialQuestion, resetSettings.balloonCount),
      gameStatus: 'ready',
      timeLeft: resetSettings.timePerQuestion,
      streak: 0,
    });
    
    setShowExplanation(false);
    setLastResult(false);
    setAnsweredQuestion(null);
  };
  
  // Get next question
  const nextQuestion = () => {
    const newQuestion = getRandomQuestion();
    setGameState(prevState => ({
      ...prevState,
      currentQuestion: newQuestion,
      balloons: createBalloons(newQuestion, settingsRef.current.balloonCount),
      timeLeft: settingsRef.current.timePerQuestion
    }));
  };

  // Start game
  const startGame = () => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.log('WebSocket not connected. Trying to connect...');
      connectWebSocket();
      
      // Show a toast notification
      toast({
        title: "Connecting to game server...",
        description: "Please wait while we connect to the game server."
      });
      
      // Set a timeout to check if connection was established
      setTimeout(() => {
        if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
          toast({
            title: "Connection failed",
            description: "Could not connect to game server. Using offline mode.",
            type: 'warning'
          });
          startOfflineGame();
        } else {
          startOnlineGame();
        }
      }, 4000);
      return;
    }
    
    startOnlineGame();
  };
  
  // Start game using WebSocket
  const startOnlineGame = () => {
    // Update UI first
    setGameState(prev => ({
      ...prev,
      score: 0,
      level: 1,
      lives: 3,
      gameStatus: 'playing',
      timeLeft: settingsRef.current.timePerQuestion,
      streak: 0
    }));
    
    // Send start game message to server
    websocketRef.current.send(JSON.stringify({
      type: 'start_game'
    }));
    
    // Request first question
    requestQuestion();
    
    toast({
      title: "Game Started!",
      description: "Pop the balloon with the correct answer!",
      type: 'success'
    });
    
    // Start animation loop
    animateGame();
  };
  
  // Start game in offline mode (using hardcoded questions)
  const startOfflineGame = () => {
    const question = getRandomQuestion();
    setGameState({
      score: 0,
      level: 1,
      lives: 3,
      currentQuestion: question,
      balloons: createBalloons(question, settingsRef.current.balloonCount),
      gameStatus: 'playing',
      timeLeft: settingsRef.current.timePerQuestion,
      streak: 0
    });
    
    toast({
      title: "Game Started (Offline Mode)!",
      description: "Pop the balloon with the correct answer!",
      type: 'info'
    });
    
    animateGame();
  };
  
  // Request a question from the server
  const requestQuestion = () => {
    if (!websocketRef.current || websocketRef.current.readyState !== WebSocket.OPEN) {
      console.log('WebSocket not connected, using local question');
      nextQuestion(); // Use local question fallback
      return;
    }
    
    websocketRef.current.send(JSON.stringify({
      type: 'request_question'
    }));
    
    console.log('Requested question from server');
  };

  // Animate the game
  const animateGame = () => {
    if (gameState.gameStatus !== 'playing') return;
    setGameState(prevState => {
      const updatedBalloons = prevState.balloons.map(balloon => ({
        ...balloon,
        y: balloon.y - balloon.speed
      }));
      const allFloatedAway = updatedBalloons.every(balloon => balloon.y + balloon.size < 0);
      if (allFloatedAway) {
        const newLives = prevState.lives - 1;
        if (newLives <= 0) {
          return {
            ...prevState,
            gameStatus: 'gameOver',
            lives: 0
          };
        }
        const newQuestion = getRandomQuestion();
        return {
          ...prevState,
          balloons: createBalloons(newQuestion, settingsRef.current.balloonCount),
          currentQuestion: newQuestion,
          lives: newLives,
          streak: 0,
          timeLeft: settingsRef.current.timePerQuestion
        };
      }
      return {
        ...prevState,
        balloons: updatedBalloons
      };
    });
    animationRef.current = requestAnimationFrame(animateGame);
  };

  // Handle balloon pop
  const handleBalloonPop = (optionIndex) => {
    if (gameState.gameStatus !== 'playing' || !gameState.currentQuestion) return;
    const poppedBalloon = gameState.balloons.find(b => b.optionIndex === optionIndex);
    if (!poppedBalloon) return;
    
    // Play pop sound
    if (popSoundRef.current) {
      popSoundRef.current.currentTime = 0;
      popSoundRef.current.play().catch(() => {});
    }
    
    // Show confetti for all balloon pops
    const gameArea = gameAreaRef.current;
    if (gameArea) {
      const rect = gameArea.getBoundingClientRect();
      const x = (poppedBalloon.x + rect.left) / window.innerWidth;
      const y = (poppedBalloon.y + rect.top) / window.innerHeight;
      
      try {
        // Create and position a separate canvas for confetti that will be removed after animation
        const canvas = document.createElement('canvas');
        canvas.className = 'balloon-pop-confetti-canvas';
        canvas.style.position = 'fixed';
        canvas.style.top = '0';
        canvas.style.left = '0';
        canvas.style.width = '100%';
        canvas.style.height = '100%';
        canvas.style.pointerEvents = 'none';
        canvas.style.zIndex = '1500';
        canvas.style.background = 'transparent';
        document.body.appendChild(canvas);
        
        const myConfetti = confetti.create(canvas, {
          resize: true,
          useWorker: true
        });
        
        // Check if answer is correct (online or offline mode)
        const isCorrect = optionIndex === gameState.currentQuestion.correctAnswer;
        
        // Different colors based on correct/incorrect
        const confettiColors = isCorrect 
          ? [poppedBalloon.colorScheme.main, '#22c55e', '#4f8cff'] // Balloon color + green + blue
          : ['#ef4444', '#f87171', '#fca5a5'];         // Different shades of red
        
        myConfetti({
          particleCount: 30,
          spread: 50,
          origin: { x, y },
          colors: confettiColors,
          disableForReducedMotion: true,
        });
        
        // Remove canvas after animation is done
        setTimeout(() => {
          if (document.body.contains(canvas)) {
            document.body.removeChild(canvas);
          }
        }, 3000);
      } catch (e) {
        console.error("Confetti error:", e);
      }
    }
    
    // If online, send answer to server; otherwise, process offline
    if (websocketRef.current && websocketRef.current.readyState === WebSocket.OPEN) {
      // Save current question before it gets replaced by the next one
      setAnsweredQuestion({...gameState.currentQuestion});
      
      // Online mode - send answer to server
      websocketRef.current.send(JSON.stringify({
        type: 'submit_answer',
        question_id: gameState.currentQuestion.id,
        option_index: optionIndex,
        time_taken: settingsRef.current.timePerQuestion - gameState.timeLeft
      }));
      
      console.log('Sent answer to server:', optionIndex);
      
      // Remove the popped balloon from UI
      setGameState(prev => ({
        ...prev,
        balloons: prev.balloons.filter(b => b.id !== poppedBalloon.id)
      }));
      
    } else {
      // Offline mode - process answer locally
      processOfflineAnswer(optionIndex, poppedBalloon);
    }
  };
  
  // Process answer locally (offline mode)
  const processOfflineAnswer = (optionIndex, poppedBalloon) => {
    // Check if answer is correct
    const isCorrect = optionIndex === gameState.currentQuestion.correctAnswer;
    
    // Save current question for explanation
    setAnsweredQuestion({...gameState.currentQuestion});
    
    // Display the result
    setLastResult(isCorrect);
    setShowExplanation(true);
    
    // Update game state based on answer
    if (isCorrect) {
      const newStreak = gameState.streak + 1;
      const streakBonus = Math.floor(newStreak / 3) * 5;
      
      setGameState(prev => ({
        ...prev,
        score: prev.score + 10 + streakBonus,
        streak: newStreak,
        balloons: prev.balloons.filter(b => b.id !== poppedBalloon.id)
      }));
      
      // Show additional confetti for correct answer
      try {
        const canvas = document.createElement('canvas');
        canvas.className = 'balloon-pop-confetti-canvas';
        canvas.style.position = 'fixed';
        document.body.appendChild(canvas);
        
        const myConfetti = confetti.create(canvas, {
          resize: true,
          useWorker: true
        });
        
        myConfetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 },
          colors: ['#22c55e', '#4f8cff', '#10b981', '#3b82f6'],
        });
        
        setTimeout(() => {
          if (document.body.contains(canvas)) {
            document.body.removeChild(canvas);
          }
        }, 3000);
      } catch (e) {
        console.error("Confetti error:", e);
      }
      
      // After a short delay, clear the explanation and set a new question
      setTimeout(() => {
        setShowExplanation(false);
        setAnsweredQuestion(null);
        nextQuestion();
      }, 3000);
    } else {
      // Wrong answer
      const newLives = gameState.lives - 1;
      
      setGameState(prev => ({
        ...prev,
        lives: newLives,
        streak: 0,
        balloons: prev.balloons.filter(b => b.id !== poppedBalloon.id)
      }));
      
      // Check if game over
      if (newLives <= 0) {
        setGameState(prev => ({
          ...prev,
          gameStatus: 'gameOver'
        }));
      } else {
        // After a short delay, clear the explanation and set a new question
        setTimeout(() => {
          setShowExplanation(false);
          setAnsweredQuestion(null);
          nextQuestion();
        }, 3000);
      }
    }
  };

  // Simple card components with scoped class names
  const Card = ({ children, className = '', ...props }) => (
    <div className={`balloon-pop-card ${className}`} {...props}>{children}</div>
  );
  
  const CardHeader = ({ children, className = '', ...props }) => (
    <div className={`balloon-pop-card-header ${className}`} {...props}>{children}</div>
  );
  
  const CardContent = ({ children, className = '', ...props }) => (
    <div className={`balloon-pop-card-content ${className}`} {...props}>{children}</div>
  );
  
  const CardFooter = ({ children, className = '', ...props }) => (
    <div className={`balloon-pop-card-footer ${className}`} {...props}>{children}</div>
  );
  
  const Badge = ({ children, color, className = '', ...props }) => (
    <span className={`balloon-pop-badge ${className}`} style={{ backgroundColor: color }} {...props}>{children}</span>
  );
  
  const Progress = ({ value, className = '', ...props }) => (
    <div className={`balloon-pop-progress ${className}`} {...props}>
      <div className="balloon-pop-progress-bar" style={{ width: `${value}%` }}></div>
    </div>
  );
  
  const Button = ({ children, onClick, className = '', ...props }) => (
    <button 
      className={`balloon-pop-button ${className}`} 
      onClick={onClick}
      {...props}
    >
      {children}
    </button>
  );

  // Try localhost as a last resort
  const tryLocalhost = () => {
    try {
      // Try a different WebSocket URL - this is a fallback URL
      const localURL = `ws://localhost:8000/wschat/balloonpop/${roomName}/`;
      console.log(`Trying localhost connection: ${localURL}`);
      setWsConnectionStatus(`Connecting to localhost...`);
      
      const ws = new WebSocket(localURL);
      websocketRef.current = ws;
      
      // Add a timeout to detect connection failures
      const connectionTimeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          console.log('Connection timeout on localhost, falling back to offline mode');
          ws.close();
          setWsConnectionStatus('All Connections Failed');
          toast({
            title: "All Connection Attempts Failed",
            description: "Using offline mode with local questions."
          });
        }
      }, 5000);
      
      ws.onopen = () => {
        clearTimeout(connectionTimeout);
        setWsConnectionStatus('Connected (localhost)');
        console.log('WebSocket connection established to localhost');
        
        // Join the game
        const joinMessage = JSON.stringify({
          type: 'join_game',
          name: `Player_${Math.floor(Math.random() * 1000)}`,
          player_id: playerIdRef.current,
          topics: settingsRef.current.categories
        });
        
        ws.send(joinMessage);
        console.log('Sent join message to localhost:', joinMessage);
      };
      
      // Set up other event handlers (onmessage, onclose, onerror)
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('Received WebSocket message (localhost):', data);
          
          // Process message with the same handlers
          switch(data.type) {
            case 'welcome_message':
              console.log('Welcome message from localhost:', data.message);
              break;
            case 'question':
            case 'new_question':
              handleWebSocketQuestion(data);
              break;
            case 'answer_result':
              handleWebSocketAnswerResult(data);
              break;
            case 'game_state':
              handleWebSocketGameState(data);
              break;
            case 'error':
              console.error('WebSocket error from localhost:', data.message);
              break;
            default:
              console.log('Unhandled message type from localhost:', data.type);
          }
        } catch (error) {
          console.error('Failed to parse WebSocket message from localhost:', error);
        }
      };
      
      ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        setWsConnectionStatus('Disconnected (localhost)');
        console.log(`WebSocket connection to localhost closed: ${event.code}`);
        setWsConnectionStatus('All Connections Failed');
        
        toast({
          title: "All Connections Failed",
          description: "Using offline mode with local questions."
        });
      };
      
      ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error (localhost):', error);
        setWsConnectionStatus('All Connections Failed');
        
        // If all connections fail, switch to offline mode
        toast({
          title: "All Connections Failed",
          description: "Using offline mode with local questions."
        });
      };
    } catch (error) {
      console.error('Error creating localhost WebSocket connection:', error);
      setWsConnectionStatus('All Connections Failed');
      
      // If all connections fail, switch to offline mode
      toast({
        title: "All Connections Failed",
        description: "Using offline mode with local questions."
      });
    }
  };

  return (
    <div className="balloon-pop-game-container">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        transition={{ duration: 0.5 }} 
        className="balloon-pop-motion-container"
      >
        <Card>
          <CardHeader className="balloon-pop-gradient-header">
            <div className="balloon-pop-header-content">
              <div>
                <div className="balloon-pop-title">STEM Balloon Pop</div>
                <div className="balloon-pop-subtitle">Pop the balloon with the correct answer!</div>
              </div>
              <div>
                {gameState.gameStatus === 'playing' && (
                  <div className="balloon-pop-badges">
                    <Badge color="#2563eb">Level: {gameState.level}</Badge>
                    <Badge color="#16a34a">Score: {Math.floor(gameState.score)}</Badge>
                    <Badge color="#dc2626">Lives: {gameState.lives}</Badge>
                    <Badge color="#f59e0b">Streak: {gameState.streak}</Badge>
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {gameState.gameStatus === 'ready' && (
              <div className="balloon-pop-start-screen">
                <h2 className="balloon-pop-start-title">Welcome to STEM Balloon Pop!</h2>
                <p className="balloon-pop-start-desc">
                  Answer STEM questions by popping the balloon with the correct answer. Be quick! The balloons will float away if you take too long.
                </p>
                <Button onClick={startGame}>Start Game</Button>
              </div>
            )}
            {gameState.gameStatus === 'playing' && (
              <div className="balloon-pop-game-content">
                {/* Timer */}
                <div className="balloon-pop-timer">
                  <div className="balloon-pop-timer-text">
                    <span>Time left:</span>
                    <span>{gameState.timeLeft} seconds</span>
                  </div>
                  <Progress value={(gameState.timeLeft / settingsRef.current.timePerQuestion) * 100} />
                </div>
                {/* Question */}
                <div className="balloon-pop-question">
                  <h3 className="balloon-pop-question-text" style={{ 
                    fontSize: '1.3rem', 
                    maxWidth: '90%', 
                    margin: '0 auto',
                    marginBottom: '15px' 
                  }}>
                    {gameState.currentQuestion?.text}
                  </h3>
                  <div className="balloon-pop-question-meta">
                    Category: {gameState.currentQuestion?.category}
                    {gameState.currentQuestion?.difficulty && (
                      <span className="balloon-pop-difficulty">• Difficulty: {gameState.currentQuestion.difficulty}</span>
                    )}
                  </div>
                </div>
                {/* Game Canvas */}
                <div 
                  ref={gameAreaRef} 
                  className="balloon-pop-game-area"
                  style={{
                    height: windowSize.width < 600 ? '400px' : '500px', // Responsive height based on window size
                  }}
                >
                  {gameState.balloons.map((balloon) => (
                    <motion.div
                      key={balloon.id}
                      className="balloon-pop-balloon"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: balloon.x - balloon.size / 2,
                        width: balloon.size,
                        height: balloon.size * 1.3,
                        zIndex: 10,
                      }}
                      initial={{ y: 350 }}
                      animate={{ y: balloon.y - balloon.size * 1.3 }}
                      transition={{ type: 'tween', duration: 0.2 }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => handleBalloonPop(balloon.optionIndex)}
                      role="button"
                      tabIndex={0}
                      aria-label={`Answer option: ${balloon.text}`}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBalloonPop(balloon.optionIndex);
                        }
                      }}
                    >
                      {/* Balloon Body */}
                      <div
                        className="balloon-pop-balloon-body"
                        style={{
                          background: `radial-gradient(circle at 30% 30%, ${balloon.colorScheme.light}, ${balloon.colorScheme.main} 60%, ${balloon.colorScheme.dark} 100%)`,
                        }}
                      >
                        {/* Balloon Reflection */}
                        <div className="balloon-pop-balloon-reflection" />
                        
                        {/* Text container - make text more readable */}
                        <div className="balloon-pop-balloon-text" style={{ 
                          background: `rgba(${balloon.colorScheme.dark.replace('rgb(', '').replace(')', '')}, 0.85)`,
                          color: balloon.colorScheme.text,
                          border: `2px solid ${balloon.colorScheme.light}`,
                          fontSize: balloon.size > 160 ? '14px' : balloon.size > 130 ? '14px' : '10px',
                          padding: '10px',
                          maxWidth: '90%',
                          maxHeight: '70%',
                          overflowY: 'auto',
                        }}>
                          {balloon.text}
                        </div>
                      </div>
                      
                      {/* Balloon Knot */}
                      <div
                        className="balloon-pop-balloon-knot"
                        style={{
                          background: `linear-gradient(to bottom, ${balloon.colorScheme.main}, ${balloon.colorScheme.dark})`,
                        }}
                      />
                      
                      {/* Balloon String */}
                      <div className="balloon-pop-balloon-string" />
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
            {gameState.gameStatus === 'gameOver' && (
              <div className="balloon-pop-game-over">
                <h2 className="balloon-pop-game-over-title">Game Over!</h2>
                <p className="balloon-pop-game-over-score">Your final score: {Math.floor(gameState.score)}</p>
                <p className="balloon-pop-game-over-level">You reached level {gameState.level}</p>
                <Button onClick={resetGame}>Play Again</Button>
              </div>
            )}
          </CardContent>
          <CardFooter>
            <div className="balloon-pop-footer-text">
              <p>Pop the balloon with the correct answer before time runs out or the balloons float away!</p>
              <p>Each correct answer increases your score and streak. Higher streaks give bonus points!</p>
            </div>
          </CardFooter>
        </Card>
      </motion.div>
      {/* Explanation Dialog */}
      <AnimatePresence>
        {showExplanation && (answeredQuestion || gameState.currentQuestion) && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="balloon-pop-explanation-overlay"
          >
            <div className={`balloon-pop-explanation-dialog ${lastResult ? 'balloon-pop-correct' : 'balloon-pop-incorrect'}`}>
              <button
                onClick={() => setShowExplanation(false)}
                className="balloon-pop-explanation-close"
                aria-label="Close explanation dialog"
              >
                ×
              </button>
              <h3 className="balloon-pop-explanation-title">
                {lastResult ? '✓ Correct!' : '✗ Incorrect!'}
              </h3>
              <p className="balloon-pop-explanation-text">
                {answeredQuestion?.explanation || gameState.currentQuestion?.explanation}
              </p>
              {lastResult && (
                <p className="balloon-pop-explanation-points">
                  +{10 + Math.floor(gameState.streak / 3) * 5} points
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Toast Notifications */}
      <div className="balloon-pop-toast-container">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: -50, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -50, scale: 0.9 }}
              className={`balloon-pop-toast balloon-pop-toast-${toast.type}`}
            >
              <div className="balloon-pop-toast-icon">
                {toast.type === 'success' && '✓'}
                {toast.type === 'error' && '✗'}
                {toast.type === 'warning' && '⚠'}
                {toast.type === 'info' && 'ℹ'}
              </div>
              <div className="balloon-pop-toast-content">
                <div className="balloon-pop-toast-title">{toast.title}</div>
                <div className="balloon-pop-toast-description">{toast.description}</div>
              </div>
              <button 
                className="balloon-pop-toast-close"
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                aria-label="Close notification"
              >
                ×
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}